// src/views/student/AddStudentModal/AddStudentModal.tsx
import React, {
  FC, useCallback, useEffect, useRef, useState,
} from 'react';
import { Checkbox } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classes from './AddStudentModal.module.less';
import AppModal from '../../../components/AppModal';
import StudentForm from '../../../components/StudentForm';
// import { UserData } from '../../../services/ApiService.types';
import { success, error } from '../../../components/Message';
import {
  addStudent, addStudentToGroup, fetchStudentGroups, setAddingStudentError,
} from '../slice/actions';
import { getIsAddingError, getIsAdding } from '../slice/selectors';
import ErrorMessage from '../../../components/ErrorMessage';
import { StudentFormData, StudentGroup } from '../../../services/ApiService.types';
import { AppDispatch } from '../../../redux/store';
import { getApiErrorCode, polishServerText } from '../../../utils/apiErrors';

type AddStudentModalProps = {
  isVisible: boolean;
  // Grupa wybrana na ekranie — okno proponuje dopisać do niej nowego ucznia. `null` — wybrani „Wszyscy”
  group: StudentGroup | null;
  hideModal: () => void;
  // Po dodaniu ucznia: czy trafił do wybranej grupy
  onAdded: (isInGroup: boolean) => void;
};

const AddStudentModal: FC<AddStudentModalProps> = ({
  isVisible, group, hideModal, onAdded,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isAdding = useSelector(getIsAdding);
  const apiError = useSelector(getIsAddingError);
  // Pole „Dopisz do grupy” zaznaczone przy każdym otwarciu okna
  const [joinGroup, setJoinGroup] = useState(true);
  // Dopisanie do grupy idzie już po dodaniu ucznia — przycisk kręci się do końca obu kroków
  const [isJoining, setIsJoining] = useState(false);
  // Enter w polu wysyła formularz także w trakcie zapisu i zamykania okna — drugi raz dodałby tego samego ucznia.
  // Blokadę zdejmuje nieudany zapis (okno zostaje do poprawki) albo ponowne otwarcie okna
  const isSubmittingRef = useRef(false);

  // Przy każdym otwarciu: pole „Dopisz do grupy” zaznaczone, formularz znowu przyjmuje wysyłkę i bez komunikatu
  // błędu z poprzedniego razu
  useEffect(() => {
    if (isVisible) {
      setJoinGroup(true);
      isSubmittingRef.current = false;
      dispatch(setAddingStudentError(null));
    }
  }, [dispatch, isVisible]);

  const cleanupError = useCallback(() => {
    dispatch(setAddingStudentError(null));
  }, [dispatch]);

  const onCancel = useCallback(() => {
    hideModal();
    cleanupError();
  }, [hideModal, cleanupError]);

  // Uczeń jest już dodany, więc nieudane dopisanie nie zatrzymuje okna — komunikat mówi, co się stało.
  // Oddaje, czy uczeń jest w grupie
  const addToGroup = useCallback(async (studentId: number, targetGroup: StudentGroup) => {
    const groupGoneMessage = t('student:add.group.gone', { group: targetGroup.name });

    try {
      const savedGroup = await dispatch(addStudentToGroup(studentId, targetGroup.id));

      if (!savedGroup) {
        error(groupGoneMessage);

        return false;
      }

      success(t('student:add.group.added', { group: savedGroup.name }));

      return true;
    } catch (e) {
      // Grupę usunięto między wczytaniem a zapisem, np. w drugiej karcie
      if (getApiErrorCode(e) === 'GROUP_NOT_FOUND') {
        error(groupGoneMessage);
        dispatch(fetchStudentGroups());
      } else {
        error(t('student:add.group.error', { group: targetGroup.name }));
      }

      return false;
    }
  }, [dispatch, t]);

  const onSubmit = useCallback(async (values: StudentFormData) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;

    const targetGroup = group && joinGroup ? group : null;
    const student = await dispatch(addStudent(values));

    // Okno zostaje otwarte; tekst serwera (przy odpowiedzi z kodem) stoi nad formularzem
    if (!student) {
      isSubmittingRef.current = false;
      error(t('student:add.messages.error'));

      return;
    }

    let isInGroup = false;

    if (targetGroup) {
      setIsJoining(true);
      isInGroup = await addToGroup(student.id, targetGroup);
      setIsJoining(false);
    } else {
      success(t('student:add.messages.success'));
    }

    // Blokada zostaje: okno jeszcze chwilę się zamyka, a Enter wciśnięty w tym czasie wysłałby formularz drugi raz
    hideModal();
    onAdded(isInGroup);
  }, [addToGroup, dispatch, group, hideModal, joinGroup, onAdded, t]);

  return (
    <AppModal title={t('student:add.title')} visible={isVisible} onCancel={onCancel}>
      {/* Tekst backendu tylko przy odpowiedzi z kodem — inne błędy zgłasza samo powiadomienie */}
      {apiError?.code && polishServerText(apiError.message) && <ErrorMessage>{apiError.message}</ErrorMessage>}
      {/* Formularz wspólny z edycją ucznia — ciaśniejsze odstępy tylko w tym oknie */}
      <div className={classes.form}>
        <StudentForm
          isLoading={isAdding || isJoining}
          onSubmit={onSubmit}
          beforeSubmit={group && (
            <Checkbox
              className={classes.groupCheckbox}
              checked={joinGroup}
              onChange={(e) => setJoinGroup(e.target.checked)}
            >
              {t('student:add.joinGroup', { group: group.name })}
            </Checkbox>
          )}
        />
      </div>
    </AppModal>
  );
};

export default AddStudentModal;
